import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'clsx'
import { useCsrf } from '../../utils/context/CsrfContext'
import { isNumber, formatPhone } from '../../utils/functions'
import * as styles from '../../styles/components/slide-out-contact-form.module.scss'
import { PANEL_ID } from './FloatingContactButton'

const TITLE_ID = 'global-contact-form-title'

const SlideOutContactForm = ({ open, onClose }) => {
  const [zipcode, setZipcode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [lookupError, setLookupError] = useState(null)
  const [consultantInfo, setConsultantInfo] = useState(null)
  const zipInputRef = useRef(null)

  const consultants = useMemo(() => {
    if (!consultantInfo) return []
    const make = (key, label) => {
      const name = consultantInfo?.[key]
      if (!name) return null
      const email = consultantInfo?.[key === 'ric' ? 'email' : `${key}_email`]
      const phone = consultantInfo?.[`${key}_phone`]
      const calendar = consultantInfo?.[`${key}_calendar`]
      const photo = consultantInfo?.[`${key}_photo`]
      const initials = name
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part[0]?.toUpperCase())
        .slice(0, 2)
        .join('') || name.slice(0, 1).toUpperCase()
      return {
        key,
        name,
        role: label,
        email,
        phone,
        calendar,
        photo,
        initials,
      }
    }
    return [
      make('ric', 'Regional Investment Consultant'),
      make('bda', 'Business Development Associate'),
    ].filter(Boolean)
  }, [consultantInfo])

  const { ensure: ensureCsrf } = useCsrf()

  const updateConsultantInfo = useCallback((data) => {
    setConsultantInfo(data)
    setLookupError(null)
    setZipcode(data?.zipcode || '')

    if (typeof window !== 'undefined') {
      if (data) {
        window.sessionStorage.setItem('consultantInfo', JSON.stringify(data))
      } else {
        window.sessionStorage.removeItem('consultantInfo')
      }
    }
  }, [])

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose()
    }
  }, [onClose])

  const handleZipChange = useCallback((e) => {
    const { value } = e.target
    if (value.length <= 5 && isNumber(value)) {
      setZipcode(value)
      setLookupError(null)
    }
  }, [])

  const handleBackdropKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClose()
    }
  }, [handleClose])

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault()
    if (zipcode.length !== 5) return

    setIsSubmitting(true)
    setLookupError(null)
    
    try {
      const csrf = await ensureCsrf()
      const res = await fetch('/api/zipcode-lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': csrf,
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          zipcode: zipcode,
          skipRecaptcha: true,
        }),
      })
      
      const json = await res.json()
      if (json.status !== 'success') {
        throw new Error(json.errors?.[0] || 'Error looking up consultant')
      }
      
      updateConsultantInfo(json.data)
      window.dataLayer?.push({ event: 'ZipSubmit', zipCode: zipcode })
    } catch (err) {
      setLookupError(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }, [zipcode, ensureCsrf, updateConsultantInfo])

  const handleEdit = useCallback(() => {
    updateConsultantInfo(null)
  }, [updateConsultantInfo])

  useEffect(() => {
    if (!open) return

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleClose()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [open, handleClose])

  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    if (!open) return undefined

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    if (typeof window === 'undefined') return

    const stored = window.sessionStorage.getItem('consultantInfo')
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        if (parsed) updateConsultantInfo(parsed)
      } catch (error) {
        window.sessionStorage.removeItem('consultantInfo')
      }
    }
  }, [open, updateConsultantInfo])

  useEffect(() => {
    if (!open) return

    if (zipInputRef.current && !consultantInfo) {
      const id = requestAnimationFrame(() => {
        zipInputRef.current?.focus()
      })
      return () => cancelAnimationFrame(id)
    }

    return undefined
  }, [open, consultantInfo])

  const hasConsultantResults = consultants.length > 0
  const zipHasError = zipcode.length > 0 && zipcode.length < 5

  return (
    <>
      <div
        className={`${styles.slideOutContactFormBackdrop} ${open ? styles.slideOutContactFormBackdropOpen : ''}`}
        role="button"
        tabIndex={open ? 0 : -1}
        onClick={handleClose}
        onKeyDown={handleBackdropKeyDown}
        aria-label="Close contact form"
        aria-hidden={!open}
      />

      <aside
        id={PANEL_ID}
        className={`${styles.slideOutContactForm} ${open ? styles.slideOutContactFormOpen : ''}`}
        aria-hidden={!open}
        aria-modal={open}
        role="dialog"
        aria-labelledby={TITLE_ID}
      >
        <div className={styles.slideOutContactFormHeader}>
          <h2 id={TITLE_ID} className={styles.slideOutContactFormTitle}>Contact Us</h2>
          <button onClick={handleClose} className={styles.slideOutContactFormCloseButton} aria-label="Close contact form">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div className={styles.slideOutContactFormContent}>
          <div className={styles.slideOutContactFormIntro}>
            <p className={styles.slideOutContactFormEyebrow}>For Institutional Investors Only</p>
            <h3 className={styles.slideOutContactFormHeadline}>Locate Your Harbor Consultant</h3>
            <p className={styles.slideOutContactFormSubtitle}>
              Enter your ZIP code to connect with your dedicated Harbor team.
            </p>
          </div>

          {!hasConsultantResults && (
            <div className={styles.slideOutContactFormSurface}>
              <form onSubmit={handleSubmit} className={styles.slideOutContactFormForm}>
                <div
                  className={cx(
                    styles.slideOutContactFormInputContainer,
                    zipHasError && styles.slideOutContactFormInputContainerError,
                    isSubmitting && styles.slideOutContactFormInputContainerBusy
                  )}
                >
                  <input
                    ref={zipInputRef}
                    placeholder="ZIP Code*"
                    value={zipcode}
                    onChange={handleZipChange}
                    className={cx(
                      styles.slideOutContactFormZipInput,
                      zipHasError && styles.slideOutContactFormZipInputError
                    )}
                    maxLength={5}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    aria-describedby={zipHasError ? `${PANEL_ID}-zip-helper` : undefined}
                  />
                  <button
                    type="submit"
                    disabled={zipcode.length < 5 || isSubmitting}
                    className={styles.slideOutContactFormSubmitButton}
                  >
                    {isSubmitting ? 'Searching...' : 'Submit'}
                  </button>
                </div>

                {zipHasError && (
                  <p className={styles.slideOutContactFormErrorText} id={`${PANEL_ID}-zip-helper`}>
                    Enter a valid 5-digit ZIP code.
                  </p>
                )}
              </form>

              <p className={styles.slideOutContactFormInstructions}>
                We use your ZIP code to introduce the Harbor professionals dedicated to your region.
              </p>

              {lookupError && (
                <div className={styles.slideOutContactFormErrorDisplay} role="alert">
                  {lookupError}
                </div>
              )}
            </div>
          )}

          {hasConsultantResults && (
            <div className={styles.slideOutContactFormSurface}>
              <div className={styles.slideOutContactFormResultHeader}>
                <span className={styles.slideOutContactFormResultBadge}>Match Found</span>
                <h4 className={styles.slideOutContactFormResultTitle}>Your Harbor consultant team</h4>
                <p className={styles.slideOutContactFormResultDescription}>
                  Reach out directly or edit your search below.
                </p>
              </div>

              <div className={styles.slideOutContactFormConsultantList}>
                {consultants.map((consultant) => (
                  <article key={consultant.key} className={styles.slideOutContactFormConsultantCard}>
                    <div className={styles.slideOutContactFormConsultant}>
                      <div className={styles.slideOutContactFormConsultantAvatar}>
                        {consultant.photo ? (
                          <img src={consultant.photo} alt={consultant.name} width="72" height="72" loading="lazy" />
                        ) : (
                          <span>{consultant.initials}</span>
                        )}
                      </div>
                      <div className={styles.slideOutContactFormConsultantBody}>
                        <div className={styles.slideOutContactFormConsultantHeader}>
                          <p className={styles.slideOutContactFormConsultantName}>{consultant.name}</p>
                          {consultant.phone && (
                            <a
                              href={`tel:${consultant.phone}`}
                              className={styles.slideOutContactFormConsultantPhone}
                            >
                              {formatPhone(consultant.phone)}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={styles.slideOutContactFormConsultantRoleRow}>
                      <p className={styles.slideOutContactFormConsultantRole}>{consultant.role}</p>
                    </div>
                    {(consultant.email || consultant.calendar) && (
                      <div className={styles.slideOutContactFormConsultantActions}>
                        {consultant.email && (
                          <a
                            href={`mailto:${consultant.email}`}
                            className={styles.slideOutContactFormConsultantAction}
                          >
                            Email
                          </a>
                        )}
                        {consultant.calendar && (
                          <a
                            href={consultant.calendar}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.slideOutContactFormConsultantAction}
                          >
                            Schedule
                          </a>
                        )}
                      </div>
                    )}
                  </article>
                ))}
              </div>

              <div className={styles.slideOutContactFormZipCodeInfo}>
                <div className={styles.slideOutContactFormZipCodeRow}>
                  <span className={styles.slideOutContactFormZipGlyph} aria-hidden="true">📍</span>
                  <p className={styles.slideOutContactFormZipCodeText}>
                    Zip code: <strong>{consultantInfo?.zipcode || zipcode}</strong>
                  </p>
                </div>
                <button onClick={handleEdit} className={styles.editButton}>
                  Edit search
                </button>
              </div>

              <p className={styles.slideOutContactFormDisclaimer}>
                The Harbor Investment Consultant listed is based on the ZIP code provided and is intended for institutional investors only.
              </p>
            </div>
          )}

          <div className={styles.slideOutContactFormFooterActions}>
            <button
              type="button"
              className={styles.slideOutContactFormCloseAction}
              onClick={handleClose}
            >
              Close Form
            </button>
          </div>

          <div className={styles.slideOutContactFormContactLink}>
            <p>
              Need additional help?{' '}<br/>
              <a href="/contact-us/" target="_blank" rel="noopener noreferrer">
                Visit our Contact page
              </a>
            </p>
          </div>
        </div>
      </aside>
    </>
  )
}

SlideOutContactForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default SlideOutContactForm
